import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  OLX_PRICE_TYPES,
  OlxPricePayload,
  OlxPriceType,
  OlxPriceTypeDropdownItem,
  PriceForm,
} from './price-editor.interface';
import { CURRENCY, CurrencyType } from '../../enum/constants';
import { OlxCategoryAttribute, OlxPrice } from '../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { Currency } from '../../interfaces';

@Component({
  selector: 'app-price-editor',
  templateUrl: './price-editor.component.html',
  styleUrl: './price-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PriceEditorComponent implements OnChanges {
  @Input() price?: OlxPricePayload | OlxPrice;
  @Input() priceAttribute?: OlxCategoryAttribute;
  @Output() formReady = new EventEmitter<FormGroup<PriceForm>>();

  protected priceTypeControl = new FormControl<OlxPriceType | null>(OlxPriceType.PRICE);
  protected priceForm!: FormGroup<PriceForm>;
  protected currencyTypes: Currency[] = CURRENCY;
  protected availableTypes: OlxPriceTypeDropdownItem[] = [];

  ngOnChanges(): void {
    this.buildComponent();
  }

  private buildComponent() {
    this.initAvailableTypes();
    this.setInitialPriceType();
    if (!this.priceForm) {
      this.initForm();
    }
    this.initCurrencyTypes();
    this.initChoice(this.priceTypeControl.value!);
    this.onPriceValueChange();
  }

  private initAvailableTypes() {
    this.availableTypes = [];

    const attrValuesCodes = this.priceAttribute?.values?.map((t) => t.code) || [];

    if (!attrValuesCodes.length) {
      this.availableTypes = OLX_PRICE_TYPES;
      return;
    }

    this.availableTypes = OLX_PRICE_TYPES.filter((t) => attrValuesCodes.includes(t.value));

    const priceType = this.availableTypes.find((t) => t.value === OlxPriceType.PRICE);
    if (priceType) {
      if (this.priceAttribute?.label === 'Ціна за добу') {
        priceType.name = 'price_per_day';
      } else {
        priceType.name = 'price';
      }
    }
  }

  private initCurrencyTypes() {
    if (!this.priceAttribute?.units?.length) {
      this.currencyTypes = CURRENCY;
      return;
    }

    const attrCurrencies = this.priceAttribute.units;

    this.currencyTypes = CURRENCY.filter((c) => {
      return attrCurrencies.some((a) => a.code === c.value);
    });
  }

  private initForm() {
    this.priceForm = new FormGroup<PriceForm>({
      value: new FormControl(this.price?.value ?? null),
      currency: new FormControl(this.price?.currency ?? CurrencyType.UAH),
      trade: new FormControl(this.price?.trade ?? false),
      negotiable: new FormControl(this.price?.negotiable ?? false),
      budget: new FormControl(this.price?.budget ?? false),
    });
    this.formReady.emit(this.priceForm);
  }

  private setInitialPriceType() {
    const availableValues = this.availableTypes.map((t) => t.value);

    const raw = this.priceForm?.getRawValue();
    const priceLike = raw ?? this.price;

    if (priceLike?.trade && availableValues.includes(OlxPriceType.EXCHANGE)) {
      this.priceTypeControl.setValue(OlxPriceType.EXCHANGE);
    } else if (priceLike?.value === 0 && availableValues.includes(OlxPriceType.FREE)) {
      this.priceTypeControl.setValue(OlxPriceType.FREE);
    } else if (availableValues.includes(OlxPriceType.PRICE)) {
      this.priceTypeControl.setValue(OlxPriceType.PRICE);
    } else {
      this.priceTypeControl.setValue(this.availableTypes[0].value);
    }
  }

  private getPriceValidators(): ValidatorFn[] {
    const validation = this.priceAttribute?.validation;
    if (!validation) return [Validators.required];

    const validators = [];

    if (validation.required) {
      validators.push(Validators.required);
    }
    if (validation.min) {
      validators.push(Validators.min(validation.min || 0));
    }
    if (validation.max) {
      validators.push(Validators.max(Number(validation.max)));
    }

    return validators;
  }

  private initChoice(type: OlxPriceType) {
    const control = this.priceForm.controls.value;

    if (type === OlxPriceType.PRICE) {
      control.setValidators(this.getPriceValidators());
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity();

    switch (this.priceTypeControl.value) {
      case OlxPriceType.EXCHANGE: {
        this.priceForm.patchValue({ trade: true, value: null, negotiable: false }, { emitEvent: false });
        break;
      }
      case OlxPriceType.FREE: {
        this.priceForm.patchValue({ value: 0, trade: false, negotiable: false }, { emitEvent: false });
        break;
      }
      case OlxPriceType.PRICE: {
        const currentValue = this.priceForm.controls.value.value;
        this.priceForm.patchValue({ trade: false, value: !currentValue ? null : currentValue }, { emitEvent: false });
        break;
      }
    }
  }

  protected onChangeType(value: OlxPriceType) {
    this.initChoice(value);
  }

  protected onPriceValueChange() {
    const availableValues = this.availableTypes.map((t) => t.value);

    const value = this.priceForm.controls.value.getRawValue();
    if (
      value === 0 &&
      availableValues.includes(OlxPriceType.FREE) &&
      this.priceTypeControl.value !== OlxPriceType.FREE
    ) {
      this.priceTypeControl.setValue(OlxPriceType.FREE);
    }
  }

  protected readonly OlxPriceType = OlxPriceType;
}
